import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { LOGO_URL } from "@/lib/api";
import { House, UsersThree, EnvelopeSimple, Question, SignOut, Shield, Bell, Newspaper, List as HamburgerIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import BannerAd from "@/components/BannerAd";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";

const links = [
  { to: "/dashboard", label: "Home", icon: House, testid: "nav-home" },
  { to: "/feed", label: "Feed", icon: Newspaper, testid: "nav-feed" },
  { to: "/discover", label: "People", icon: UsersThree, testid: "nav-discover" },
  { to: "/messages", label: "Messages", icon: EnvelopeSimple, testid: "nav-messages" },
  { to: "/support", label: "Support", icon: Question, testid: "nav-support" },
];

// Also mirrored in the mobile slide-out drawer so smaller-screen users get
// direct access to the same shortcuts as the Dashboard "Quick actions" panel.
const QUICK_LINKS = [
  { to: "/websites/new", label: "Submit Website", testid: "mobile-quick-submit-site" },
  { to: "/splash", label: "Splash Pages", testid: "mobile-quick-splash-pages" },
  { to: "/start-pages", label: "Start Pages", testid: "mobile-quick-start-pages" },
  { to: "/buy-credits", label: "Buy Credits", testid: "mobile-quick-buy-credits" },
  { to: "/buy-upgrades", label: "Buy Upgrades", testid: "mobile-quick-buy-upgrades" },
  { to: "/buy-banners", label: "Buy Banners", testid: "mobile-quick-buy-banners" },
  { to: "/my-banners", label: "My Banners", testid: "mobile-quick-my-banners" },
  { to: "/buy-text-ads", label: "Buy Text Ads", testid: "mobile-quick-buy-text-ads" },
  { to: "/my-text-ads", label: "My Text Ads", testid: "mobile-quick-my-text-ads" },
  { to: "/surf-drawn-winners", label: "Surf Drawn Winners", testid: "mobile-quick-surf-drawn-winners" },
  { to: "/my-rotors", label: "TE Website Rotors", testid: "mobile-quick-rotors" },
  { to: "/referrals", label: "Referrals", testid: "mobile-quick-referrals" },
  { to: "/statistics", label: "Stats", testid: "mobile-quick-statistics" },
];

export default function DashboardLayout({ children }) {
  const { user, logout, siteSettings } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [dmUnread, setDmUnread] = useState(0);

  useEffect(() => {
    let mounted = true;
    async function loadNotifs() {
      try {
        const [notifs, dm] = await Promise.all([
          api.get("/notifications"),
          api.get("/messages/unread-count").catch(() => ({ data: { unread: 0 } })),
        ]);
        if (mounted) {
          setUnread(notifs.data.filter((n) => !n.read).length);
          setDmUnread(dm.data?.unread || 0);
        }
      } catch {}
    }
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 flex items-center h-16 sm:h-24 gap-2">
          {/* Mobile hamburger — opens a slide-out drawer with the full nav */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                className="md:hidden h-10 w-10 -ml-1 inline-flex items-center justify-center rounded-full text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
                aria-label="Open navigation"
              >
                <HamburgerIcon size={20} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              data-testid="mobile-menu-drawer"
              className="w-[85%] max-w-xs bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 p-0 flex flex-col"
            >
              <SheetHeader className="px-4 py-4 border-b border-slate-200 dark:border-slate-800 flex-row items-center gap-3 space-y-0">
                <img src={siteSettings?.logo_url || LOGO_URL} alt="logo" className="h-10" />
                <SheetTitle className="text-base font-black tracking-tight text-left flex-1" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
                  Menu
                </SheetTitle>
              </SheetHeader>

              {user && (
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-sm font-black overflow-hidden">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="h-10 w-10 object-cover" />
                    ) : (
                      (user.username || "?").slice(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold inline-flex items-center gap-1 truncate">
                      {user.username}<UserBadge user={user} size={12} />
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      {user.membership} account
                    </div>
                  </div>
                </div>
              )}

              <nav className="flex-1 overflow-y-auto py-0 flex flex-col text-center" data-testid="mobile-menu-nav">
                {links.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.testid}`}
                      className={({ isActive }) =>
                        `block w-full px-5 py-4 text-xl font-black tracking-tight border-b border-slate-200 dark:border-slate-800 text-center ${
                          isActive
                            ? "bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300"
                            : "text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800"
                        }`
                      }
                    >
                      <span className="inline-flex items-center gap-2 justify-center">
                        {l.label}
                        {l.to === "/messages" && dmUnread > 0 && (
                          <span className="bg-blue-600 text-white text-[10px] font-black rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">
                            {dmUnread}
                          </span>
                        )}
                      </span>
                    </NavLink>
                  </SheetClose>
                ))}

                {/* Quick Actions — mirrored from the Dashboard aside */}
                <div className="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 text-center">
                  Quick actions
                </div>
                {QUICK_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={l.testid}
                      className="block w-full px-5 py-4 text-xl font-black tracking-tight text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-800 text-center"
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}

                <SheetClose asChild>
                  <NavLink
                    to="/profile"
                    data-testid="mobile-nav-profile"
                    className="block w-full px-5 py-4 text-xl font-black tracking-tight text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 border-b border-slate-200 dark:border-slate-800 text-center"
                  >
                    Profile
                  </NavLink>
                </SheetClose>
                {user?.role === "admin" && (
                  <SheetClose asChild>
                    <NavLink
                      to="/admin"
                      data-testid="mobile-nav-admin"
                      className="block w-full px-5 py-4 text-xl font-black tracking-tight text-yellow-800 dark:text-yellow-300 hover:bg-yellow-50 dark:hover:bg-yellow-500/10 border-b border-slate-200 dark:border-slate-800 text-center"
                    >
                      Admin console
                    </NavLink>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <button
                    data-testid="mobile-nav-logout"
                    onClick={async () => { await logout(); navigate("/"); }}
                    className="block w-full px-5 py-4 text-xl font-black tracking-tight text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 border-b border-slate-200 dark:border-slate-800 text-center"
                  >
                    Sign out
                  </button>
                </SheetClose>
              </nav>

              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 text-center">
                <SheetClose asChild>
                  <Link
                    to="/terms"
                    className="text-xs text-slate-500 dark:text-slate-400 hover:underline"
                  >
                    Terms of Service &amp; Privacy Policy
                  </Link>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>

          <NavLink to="/dashboard" className="flex items-center pr-2 sm:pr-4">
            <img src={siteSettings?.logo_url || LOGO_URL} alt="logo" className="h-12 sm:h-20" />
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    isActive
                      ? "bg-green-600 text-white shadow-sm"
                      : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`
                }
              >
                <l.icon size={18} weight="bold" /> {l.label}
                {l.to === "/messages" && dmUnread > 0 && (
                  <span data-testid="nav-messages-unread" className="ml-1 bg-blue-600 text-white text-[10px] font-black rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center">
                    {dmUnread}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="nav-notifications-btn" onClick={() => navigate("/notifications")} className="relative h-9 w-9 inline-flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors duration-200">
              <Bell size={18} weight="bold" />
              {unread > 0 && <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-950 text-[10px] font-black rounded-full h-5 min-w-5 px-1 flex items-center justify-center">{unread}</span>}
            </button>
            <ThemeToggle />
            {user?.role === "admin" && (
              <button data-testid="nav-admin-btn" onClick={() => navigate("/admin")} className="hidden sm:inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-bold px-4 py-2 text-sm transition-colors duration-200">
                <Shield size={16} weight="fill" /> Admin
              </button>
            )}
            <button
              data-testid="nav-profile-btn"
              onClick={() => navigate("/profile")}
              className="hidden sm:flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1.5 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center text-xs font-black overflow-hidden">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-7 w-7 object-cover" />
                ) : (
                  (user?.username || "?").slice(0, 1).toUpperCase()
                )}
              </div>
              <div className="text-left leading-tight hidden sm:block">
                <div className="text-xs font-semibold inline-flex items-center gap-1">{user?.username}<UserBadge user={user} size={10} /></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{user?.membership} account</div>
              </div>
            </button>
            <button data-testid="nav-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-red-500 hover:text-white transition-colors duration-200" title="Logout">
              <SignOut size={16} weight="bold" />
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">{children}</main>
      <BannerAd />
      <footer className="border-t border-slate-200 dark:border-slate-800 py-6 text-center text-sm text-slate-500 dark:text-slate-400 space-y-1">
        <div>{siteSettings?.footer_text || "© 2026 Start-A-TE"}</div>
        <div>
          <Link
            to="/terms"
            data-testid="footer-terms-link"
            className="hover:underline text-slate-500 dark:text-slate-400"
          >
            Terms of Service &amp; Privacy Policy
          </Link>
        </div>
        <div>
          <a
            data-testid="footer-mwd-link"
            href="https://mikeswebdevelopments.com"
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs font-bold tracking-widest uppercase text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Built by MWD
          </a>
        </div>
      </footer>
    </div>
  );
}
